<template>
  <div>
    <b-row v-if="showHeading || showColumnLabels" class="mb-75">
      <b-col
        v-if="showHeading"
        cols="12"
        :md="structureMode ? 5 : 4"
        class="mb-50 mb-md-0"
      >
        <b-form-group
          :label="structureMode ? 'Structure title' : 'Table heading'"
          :label-for="headingInputId"
          class="mb-0"
        >
          <b-form-input
            :id="headingInputId"
            size="sm"
            :value="headingValue"
            :disabled="disabled"
            :placeholder="structureMode ? 'e.g. Salary Structure Above 182 Meters' : 'Annexure A — Salary Structure'"
            @input="patchHeading($event)"
          />
          <b-form-text v-if="structureMode">
            Shown above this table on Annexure A.
          </b-form-text>
        </b-form-group>
      </b-col>
      <b-col
        v-if="showColumnLabels"
        cols="12"
        :md="showHeading ? (structureMode ? 7 : 8) : 12"
      >
        <b-form-group label="Column headers" class="mb-0">
          <b-row>
            <b-col cols="4">
              <b-form-input
                size="sm"
                :value="value.columnLabels.component"
                :disabled="disabled"
                placeholder="Components"
                @input="patchColumnLabel('component', $event)"
              />
            </b-col>
            <b-col cols="4">
              <b-form-input
                size="sm"
                :value="value.columnLabels.monthly"
                :disabled="disabled"
                placeholder="Monthly (₹)"
                @input="patchColumnLabel('monthly', $event)"
              />
            </b-col>
            <b-col cols="4">
              <b-form-input
                size="sm"
                :value="value.columnLabels.yearly"
                :disabled="disabled"
                placeholder="Yearly (₹)"
                @input="patchColumnLabel('yearly', $event)"
              />
              <b-form-text>Yearly auto-calculated (× 12).</b-form-text>
            </b-col>
          </b-row>
        </b-form-group>
      </b-col>
    </b-row>

    <template v-if="showRows">
      <div class="d-flex justify-content-between align-items-center mb-75">
        <span class="text-muted small">
          Reorder rows, set monthly amounts, and pick row style.
        </span>
        <b-button
          v-if="!disabled"
          size="sm"
          variant="primary"
          class="oc-add-row-btn"
          @click="$emit('add-row')"
        >
          <feather-icon icon="PlusIcon" size="14" class="mr-25" />
          Add row
        </b-button>
      </div>

      <div class="oc-salary-table-wrap">
        <b-table
          :items="value.rows"
          :fields="tableFields"
          small
          responsive
          head-variant="light"
          class="oc-salary-table mb-1"
          tbody-tr-class="oc-salary-table-row"
        >
        <template #cell(order)="data">
          <b-button-group v-if="!disabled" size="sm">
            <b-button
              variant="outline-secondary"
              :disabled="data.index === 0"
              @click="$emit('move-row', data.index, 'up')"
            >
              <feather-icon icon="ChevronUpIcon" size="14" />
            </b-button>
            <b-button
              variant="outline-secondary"
              :disabled="data.index === value.rows.length - 1"
              @click="$emit('move-row', data.index, 'down')"
            >
              <feather-icon icon="ChevronDownIcon" size="14" />
            </b-button>
          </b-button-group>
        </template>
        <template #cell(component)="data">
          <b-form-input
            size="sm"
            :value="data.item.component"
            :disabled="disabled"
            placeholder="Component name"
            @input="patchRow(data.index, 'component', $event)"
          />
        </template>
        <template #cell(monthly)="data">
          <b-form-input
            size="sm"
            :value="data.item.monthly"
            :disabled="disabled"
            placeholder="e.g. 12,324"
            @input="patchRow(data.index, 'monthly', $event)"
          />
        </template>
        <template #cell(yearly)="data">
          <b-form-input
            size="sm"
            :value="data.item.yearly"
            readonly
            tabindex="-1"
            class="bg-light"
            :disabled="disabled"
            placeholder="Auto × 12"
          />
        </template>
        <template #cell(rowType)="data">
          <b-form-select
            size="sm"
            :value="data.item.rowType || 'normal'"
            :disabled="disabled"
            :options="rowTypeOptions"
            @change="patchRow(data.index, 'rowType', $event)"
          />
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="!disabled && value.rows.length > 1"
            size="sm"
            variant="link"
            class="text-danger p-0"
            @click="$emit('remove-row', data.index)"
          >
            <feather-icon icon="Trash2Icon" size="14" />
          </b-button>
        </template>
        </b-table>
      </div>
    </template>

    <div v-if="showNotes" class="oc-footnotes-section mt-1">
      <h6 class="oc-footnotes-title mb-75">
        <feather-icon icon="FileTextIcon" size="14" class="mr-50" />
        Footnotes below the table
      </h6>
      <b-row>
        <b-col
          v-for="noteDef in noteFieldDefs"
          :key="noteDef.key"
          cols="12"
          lg="6"
          class="mb-75"
        >
          <div class="oc-footnote-card h-100">
          <div class="d-flex justify-content-between align-items-center mb-50">
            <span class="small font-weight-bold">{{ noteDef.label }}</span>
            <b-form-checkbox
              switch
              :checked="noteEntry(noteDef.key).show"
              :disabled="disabled"
              @change="patchNote(noteDef.key, 'show', $event)"
            />
          </div>
          <b-row v-if="noteEntry(noteDef.key).show">
            <b-col cols="12" sm="4" class="mb-50 mb-sm-0">
              <b-form-group label="Label prefix" label-class="small" class="mb-0">
                <b-form-input
                  size="sm"
                  :value="noteEntry(noteDef.key).prefix"
                  :disabled="disabled"
                  :placeholder="noteDef.defaultPrefix"
                  @input="patchNote(noteDef.key, 'prefix', $event)"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="8">
              <b-form-group label="Note text" label-class="small" class="mb-0">
                <b-form-textarea
                  size="sm"
                  rows="2"
                  :value="noteEntry(noteDef.key).text"
                  :disabled="disabled"
                  :placeholder="noteDef.placeholder"
                  @input="patchNote(noteDef.key, 'text', $event)"
                />
              </b-form-group>
            </b-col>
          </b-row>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BButtonGroup,
  BTable,
  BFormGroup,
  BFormInput,
  BFormText,
  BFormTextarea,
  BFormSelect,
  BFormCheckbox,
} from 'bootstrap-vue'
import {
  normalizeSalaryNoteEntry,
  calcYearlyFromMonthly,
  inferSalaryRowType,
} from '@/utils/offer-template-editor'

const NOTE_FIELD_DEFS = [
  { key: 'professionalTax', label: 'Professional Tax', defaultPrefix: 'Note:', placeholder: 'Professional TAX note...' },
  { key: 'lwf', label: 'Labour Welfare Fund', defaultPrefix: 'Note:', placeholder: 'LWF note...' },
  { key: 'ppe', label: 'PPE deduction', defaultPrefix: '', placeholder: 'PPE kit note...' },
  { key: 'incentive', label: 'Incentive', defaultPrefix: 'Note:', placeholder: 'Incentive note...' },
  { key: 'additionalNotes', label: 'Additional notes', defaultPrefix: '', placeholder: 'One note per line...' },
]

export default {
  name: 'OfferSalaryAnnexureEditor',
  components: {
    BRow,
    BCol,
    BButton,
    BButtonGroup,
    BTable,
    BFormGroup,
    BFormInput,
    BFormText,
    BFormTextarea,
    BFormSelect,
    BFormCheckbox,
  },
  props: {
    value: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    /** When true, edit structure.title instead of annexure heading. */
    structureMode: { type: Boolean, default: false },
    showHeading: { type: Boolean, default: true },
    showColumnLabels: { type: Boolean, default: true },
    showRows: { type: Boolean, default: true },
    showNotes: { type: Boolean, default: true },
    headingInputId: { type: String, default: 'oc-salary-heading' },
  },
  data() {
    return {
      noteFieldDefs: NOTE_FIELD_DEFS,
      rowTypeOptions: [
        { value: 'normal', text: 'Normal' },
        { value: 'highlight', text: 'Bold row' },
        { value: 'ctc', text: 'Total / CTC' },
      ],
    }
  },
  computed: {
    headingValue() {
      if (this.structureMode) {
        return this.value.title != null ? this.value.title : (this.value.heading || '')
      }
      return this.value.heading || ''
    },
    tableFields() {
      const labels = this.value.columnLabels || {}
      const monthly = labels.monthly || 'Monthly (₹)'
      const yearly = labels.yearly || 'Yearly (₹)'
      const fields = []
      if (!this.disabled) fields.push({ key: 'order', label: '', thStyle: { width: '72px' } })
      fields.push(
        { key: 'component', label: labels.component || 'Component' },
        { key: 'monthly', label: monthly },
        { key: 'yearly', label: yearly },
        { key: 'rowType', label: 'Style', thStyle: { width: '120px' } },
      )
      if (!this.disabled) fields.push({ key: 'actions', label: '', thStyle: { width: '44px' } })
      return fields
    },
  },
  methods: {
    noteEntry(key) {
      const def = NOTE_FIELD_DEFS.find(n => n.key === key)
      return normalizeSalaryNoteEntry(this.value.notes && this.value.notes[key], def ? def.defaultPrefix : 'Note:')
    },
    patch(field, val) {
      this.$emit('input', { ...this.value, [field]: val })
    },
    patchHeading(val) {
      if (this.structureMode) {
        this.$emit('input', { ...this.value, title: val })
        return
      }
      this.patch('heading', val)
    },
    patchColumnLabel(key, val) {
      this.$emit('input', { ...this.value, columnLabels: { ...(this.value.columnLabels || {}), [key]: val } })
    },
    patchRow(index, field, val) {
      const rows = this.value.rows.map((row, i) => {
        if (i !== index) return row
        if (field === 'monthly') return { ...row, monthly: val, yearly: calcYearlyFromMonthly(val) }
        if (field === 'component') return { ...row, component: val, rowType: inferSalaryRowType(val) }
        return { ...row, [field]: val }
      })
      this.$emit('input', { ...this.value, rows })
    },
    patchNote(key, field, val) {
      const current = this.noteEntry(key)
      this.$emit('input', {
        ...this.value,
        notes: { ...this.value.notes, [key]: { ...current, [field]: val } },
      })
    },
  },
}
</script>

<style scoped>
.oc-salary-table-wrap {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 0.45rem;
  overflow: hidden;
}

.oc-salary-table {
  margin-bottom: 0 !important;
}

.oc-salary-table >>> thead th {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6e6b7b;
  background: #f8f9fb !important;
  border-bottom: 2px solid #eef0f3;
  white-space: nowrap;
  padding: 0.55rem 0.65rem;
}

.oc-salary-table >>> tbody td {
  vertical-align: middle;
  padding: 0.45rem 0.65rem;
  border-color: #f3f4f6;
}

.oc-salary-table >>> tbody tr:hover {
  background: rgba(0, 186, 176, 0.04);
}

.oc-add-row-btn {
  font-weight: 600;
  white-space: nowrap;
}

.oc-footnotes-title {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #2c2c2c;
}

.oc-footnote-card {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 0.45rem;
  padding: 0.65rem 0.75rem;
}
</style>
